import bytes from 'bytes'

import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { BuilderMenuItem, BuilderPage, ColumnAdapter } from '@makyoapp/makyo-common'

export function removeStar(path: string) {
  return path.replaceAll(/\/\*$/gi, '')
}

export function replaceIdAndRemoveStar(path: string, replacement: string) {
  return removeStar(path).replaceAll(/:[a-z]+/gi, replacement)
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function sanitizeFileName(name: string) {
  // Regular expression to match disallowed characters
  const regex = /[^\p{L}0-9_\-. ]/gu

  // Replace disallowed characters with an empty string
  return name.replace(regex, '')
}

export function getRandomInt(min = 10, max = 200) {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min) + min) // The maximum is exclusive and the minimum is inclusive
}

export function genIdent<T extends Record<string, unknown>>(
  samples: T[],
  key: string,
  index: number = 1,
  prefix = '',
  suffix = ''
): string {
  const name = `${prefix}${index}${suffix}`
  if (!samples || samples.length === 0) return name
  const idx = samples.findIndex((sample) => `${sample[key]}` === name)
  return idx === -1 ? name : genIdent(samples, key, index + 1, prefix, suffix)
}

/**
 * Convert bytes into readable size
 */
export function formatBytesToSize(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const dcm = decimals < 0 ? 0 : decimals
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dcm)) + ' ' + sizes[i]
}

export function getBytesBySize(bytes: number, metric: 'B' | 'KB' | 'MB' | 'GB' | 'TB' = 'B', decimals = 2) {
  if (bytes === 0) return '0'

  const sizes = { B: 0, KB: 1, MB: 2, GB: 3, TB: 4 }
  const i = sizes[metric]

  return parseFloat((bytes / Math.pow(1024, i)).toFixed(decimals))
}

/**
 * Helper functions for removing the follows:
 * - leading & trailing spaces
 * - double whitespaces between words
 */
export function cleanUpString(str: string) {
  return str.replace(/\s+/g, ' ').trim()
}

/**
 * File size formatter
 */
export function getFileSize(b: number) {
  return bytes(b, {
    thousandsSeparator: ',',
    unitSeparator: ' ',
    decimalPlaces: 2,
  })
}

/**
 * Check if a value is empty
 *
 * @param value - The value to check
 * @returns True if the value is empty, false otherwise
 */
export function isEmpty(value: any) {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0) ||
    (value instanceof Date && isNaN(value.getTime()))
  )
}

/**
 * Debounce function - this should replace lodash.debounce.
 *
 * @param func - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced function
 */
export function debounce<F extends (...args: any[]) => void>(func: F, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: Parameters<F>): void => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return debounced
}

export function createSlug(text: string, defaultSlug: string = ''): string {
  let preformatted = text.trimEnd() + (text.endsWith(' ') ? '-' : '')
  const slug = preformatted
    .toLowerCase() // Convert the string to lowercase letters
    .normalize('NFD') // The normalize() method returns the Unicode Normalization Form of a given string.
    .trim() // Remove whitespace from both sides of a string
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text

  return slug || defaultSlug
}

export const getFileTypeFromId = (id?: string) => {
  const parts = (id ?? '').split('-')
  const fileType = parts.find((part) => Object.values(FileCategory).includes(part as FileCategory))

  if (!fileType && parts.includes('file')) {
    return Object.values(FileCategory)
  }

  const fileTypes: FileCategory[] = fileType ? [fileType as FileCategory] : [FileCategory.Other]
  return fileTypes
}

/**
 * Array reorder function for grid columns or dnd
 */
export function arrayReorder<T>(prevArray: T[], startIndex: number, endIndex: number): T[] {
  let newArray = [...prevArray]
  let removed = newArray.splice(startIndex, 1)
  newArray.splice(endIndex, 0, ...removed)
  return newArray
}

/**
 * cleanUpText function for removing images from text
 * @param input - The input text to clean up
 * @returns The cleaned up text
 */
export const cleanUpText = (input: string): string => {
  if (!input || typeof input !== 'string') return ''

  const imageRegex = /!\[.*?\]\(.*?\)/g
  return input?.replace(imageRegex, '').trim()
}

/**
 * validateImageUrl function to check if a given URL is a valid image URL
 * @param url - The input URL string to validate
 * @returns The validated image URL if valid, otherwise empty string
 */
export function validateImageUrl(url: string): string {
  try {
    // Create a URL object to validate the structure
    const parsedUrl = new URL(url)

    // Check if URL has a valid protocol and ends with a common image extension
    const isValidProtocol = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
    const isImageExtension = /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(parsedUrl.pathname)

    const isImageHost = /\.(unsplash\.com|imgur\.com|cloudinary\.com)/i.test(parsedUrl.hostname)

    if (isValidProtocol && (isImageExtension || isImageHost)) {
      return url
    }
  } catch (e) {
    // If URL parsing fails, it's not a valid URL
  }

  return '' // Return empty string if URL is invalid or not an image
}

export const toPascalCase = (str: string): string =>
  str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')

export const dashifyString = (str: string): string => {
  if (!str) return ''
  const trimmedInput = str.trim()
  return trimmedInput.replace(/\s+/g, '-')
}

export const isValidNumber = (value: number | undefined): boolean => {
  return Number.isFinite(value) && value !== 0
}

export const generateMarkersHash = (markers: Record<string, any> | Record<string, any>[]) => {
  const markersArray = Array.isArray(markers) ? markers : [markers]
  return markersArray.reduce((hash, marker) => {
    const essentialData = `${marker.image}|${marker.description}|${marker.image}|${marker.info}|${marker.subtitle}|${marker.title}|${marker.subtitle}|${marker.theme}|${marker.avatar}|${marker.latitude}|${marker.longitude}`
    return (
      hash +
      essentialData.split('').reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0)
        return a & a
      }, 0)
    )
  }, 0)
}

/**
 * @note
 * The instanceof File check might not be working because the File object
 * could be losing its prototype chain during state management in React.
 */
export const isFile = (value: any): value is File =>
  value instanceof File ||
  (typeof value === 'object' && value !== null && 'name' in value && 'size' in value && 'type' in value)

export const isMenuItemVisible = (menuItem: BuilderMenuItem, checkVisibilityFn: (visibility?: any) => boolean) => {
  return checkVisibilityFn(menuItem.visibility)
}

/**
 * Generate a slug with the id of the row
 */
export function generatePageSlug(pages: BuilderPage[], pageId: string, pageTitle: string) {
  const randomSlug = Math.random().toString(36).substring(2, 8)
  let slug = createSlug(pageTitle, `page-${randomSlug}`)

  // find the page with the same slug
  const pagesWithSameSlug = pages.filter((page) => page.id !== pageId && page.slug === slug)

  if (pagesWithSameSlug.length > 0) {
    slug = `${slug}-${pagesWithSameSlug.length + 1}`
  }

  return slug
}

export function getTableColumnsWithRowId(columns: ColumnAdapter[]) {
  return [{ id: '_id', type: ColumnTypes.RowID, name: 'Row ID' } as ColumnAdapter, ...(columns || [])]
}

export const validSliderValue = (value: number | number[], minValue: number = 0, maxValue: number = 100): number[] => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.map((item) => Math.min(Math.max(item, minValue), maxValue))
  }

  if (value >= minValue && value <= maxValue) {
    return [value]
  }

  const validValue = Math.min(Math.max(value, minValue), maxValue)
  return [validValue / maxValue]
}

export const parseLabel = (label: string): string => {
  try {
    const parsed = JSON.parse(label)

    if (Array.isArray(parsed) && parsed[0]?.type === 'paragraph' && Array.isArray(parsed[0]?.children)) {
      return parsed[0].children[0].text
    }

    return label
  } catch (e) {
    return label
  }
}

export const capitalize = (str?: string) => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}
